import { useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { startAutorizarServicio, startCreaAsistenciaInicial, setCrearServiciosV2Captura } from "../../../context/actions/crearServiciosV2Actions";
import { notification } from "../../../context/actions/notificationActions";
import { composeErrorNotification } from "../../../utils/composeNotification";
import { cleanCaptura } from "../../../context/actions/capturaActions";
import { cleanPolizario } from "../../../context/actions/polizarioActions";
import { agentesCuentaEspecialGS, normalizaAgenteGS } from "../../../constants/cons";

const buildPayload = (captura, user, usuarioAutorizaMasServiciosID, noEncontradoPolizario, fechaLlamadaIniciada, gsegurosEmision) => {
  const esCuentaEspecialGS =
    gsegurosEmision?.cliente_vip?.toLowerCase() === "si" ||
    gsegurosEmision?.condiciones_especiales?.toLowerCase() === "si" ||
    agentesCuentaEspecialGS.includes(normalizaAgenteGS(gsegurosEmision?.agente));

  return {
    nombre: captura.nombre,
    apellidoPaterno: captura.apellidoPaterno,
    apellidoMaterno: captura.apellidoMaterno,
    telefono: captura.telefono,
    validador1: captura.validador1,
    validador2: captura.validador2,
    tipoPersona: captura.tipoPersona || "Asegurado",
    servicioID: captura.servicioID,
    nombreAjustador: captura.nombreAjustador,
    usuarioID: user?.usuarioID,
    usuarioAutorizaMasServiciosID,
    campaniaID: captura.campaniaID,
    fechaLlamadaIniciada,
    telefonoEsWA: Boolean(captura.telefonoEsWA),
    noEncontradoPolizario,
    ...(esCuentaEspecialGS && { CuentaEspecialGS: "SI" }),
  };
};

export const useAutorizacion = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { captura } = useSelector((state) => state.asistenciasReducer);
  const { user } = useSelector((state) => state.userReducer);
  const { gsegurosEmision } = useSelector((state) => state.polizarioReducer);

  const navegarTrasCrea = (asistenciaID, noEncontradoPolizario) => {
    const agenteLimpio = gsegurosEmision?.agente?.trim()?.replace(/\s+/g, ' ') || "";
    const numero_serie = gsegurosEmision?.numero_serie || "";
    const ct_uid = captura.ct_uid;
    
    dispatch(cleanCaptura());
    dispatch(cleanPolizario());
    
    dispatch(setCrearServiciosV2Captura({ 
      noEncontradoPolizario,
      agente: agenteLimpio,
      numero_serie,
      ct_uid
    }));
    history.push(`/editarServicioV2/${asistenciaID}`);
  };

  const validarPolizario = useCallback(() => {
    if (!captura.polizarioBloqueado) return true;
    dispatch(notification(composeErrorNotification(
      "No se puede generar servicio para este folio."
    )));
    return false;
  }, [captura.polizarioBloqueado, dispatch]);

  const autorizarServicio = useCallback(async (claveSupervisor, fechaLlamadaIniciada) => {
    if (!validarPolizario()) return { success: false };

    if (!claveSupervisor) {
      dispatch(notification(composeErrorNotification("Clave requerida", "Ingresa clave de supervisor")));
      return { success: false };
    }

    const resultadoAutorizacion = await dispatch(startAutorizarServicio(claveSupervisor));
    if (!resultadoAutorizacion?.success) return resultadoAutorizacion;

    const noEncontradoPolizario = captura.noEncontradoPolizario ?? false;
    const usuarioAutorizaMasServiciosID = resultadoAutorizacion.data?.usuarioID || user?.usuarioID;
    const payload = buildPayload(captura, user, usuarioAutorizaMasServiciosID, noEncontradoPolizario, fechaLlamadaIniciada, gsegurosEmision);

    const resultado = await dispatch(startCreaAsistenciaInicial(payload));
    if (resultado?.success && resultado?.data)
      navegarTrasCrea(resultado.data, noEncontradoPolizario);

    return resultado;
  }, [dispatch, captura, user, history, validarPolizario, gsegurosEmision]);

  const crearAsistenciaSinAutorizacion = useCallback(async (fechaLlamadaIniciada) => {
    if (!validarPolizario()) return { success: false };

    const noEncontradoPolizario = captura.noEncontradoPolizario ?? false;
    const payload = buildPayload(captura, user, user?.usuarioID, noEncontradoPolizario, fechaLlamadaIniciada, gsegurosEmision);

    const resultado = await dispatch(startCreaAsistenciaInicial(payload));
    if (resultado?.success && resultado?.data)
      navegarTrasCrea(resultado.data, noEncontradoPolizario);

    return resultado;
  }, [dispatch, captura, user, history, validarPolizario, gsegurosEmision]);

  return { autorizarServicio, crearAsistenciaSinAutorizacion };
};
